<template>
  <CardComponent :icon="PhLockSimple" title="Session" :success="session.is_current">
    <template #buttons>
      <ButtonElement
        v-if="!session.is_current"
        color="danger"
        :icon="PhTrash"
        size="small"
        :disabled="!cacheStore.online"
        bordered
        @click="confirmDeleteSession"
        >Supprimer</ButtonElement
      >
    </template>
    <!-- Première connexion -->
    <CardInfoComponent v-if="session.premiere_connexion" title="Date de création">
      <PhSignIn />
      <span
        >{{ frenchDate(session.premiere_connexion) }}
        {{ frenchTime(session.premiere_connexion) }}</span
      >
    </CardInfoComponent>

    <!-- Dernière connexion -->
    <CardInfoComponent v-if="session.derniere_connexion" title="Date de dernière connexion">
      <PhCalendar />
      <span
        >{{ frenchDate(session.derniere_connexion) }}
        {{ frenchTime(session.derniere_connexion) }}</span
      >
    </CardInfoComponent>

    <!-- Appareil -->
    <CardInfoComponent v-if="session.appareil" title="Appareil">
      <component :is="getDeviceIcon(session.appareil)" />
      <span>
        {{ session.appareil.brand === 'Generic_Android' ? 'Android' : session.appareil.brand }}
        {{ session.appareil.family === 'K' ? '' : session.appareil.family }}
        <template v-if="session.appareil.family !== session.appareil.model">
          {{ session.appareil.model }}</template
        >
      </span>
    </CardInfoComponent>

    <!-- Navigateur -->
    <CardInfoComponent v-if="session.navigateur" title="Navigateur">
      <component :is="getIcon(session.navigateur)" />
      <span>{{ getVersion(session.navigateur) }}</span>
    </CardInfoComponent>

    <CardInfoComponent v-if="session.ip" full-width title="Adresse IP">
      <PhNetwork />
      <span>{{ session.ip }}</span>
    </CardInfoComponent>
  </CardComponent>
  <ConfirmElement ref="confirmRef" @yes="deleteSession" />
</template>

<script setup lang="ts">
import {
  PhAndroidLogo,
  PhAppleLogo,
  PhBrowser,
  PhCalendar,
  PhCompass,
  PhDeviceMobile,
  PhDeviceTablet,
  PhGoogleChromeLogo,
  PhLaptop,
  PhLinuxLogo,
  PhLockSimple,
  PhNetwork,
  PhSignIn,
  PhTrash,
  PhWindowsLogo,
} from '@phosphor-icons/vue'
import type { Device, SessionList, Version } from '@/interfaces/Session.ts'
import { type PropType, useTemplateRef } from 'vue'
import frenchDate from '@/mixins/getFrenchDate.ts'
import ButtonElement from '@/components/ButtonElement.vue'
import frenchTime from '@/mixins/getFrenchTime.ts'
import CardComponent from '@/components/card/CardComponent.vue'
import CardInfoComponent from '@/components/card/CardInfoComponent.vue'
import ConfirmElement from '@/components/ConfirmElement.vue'
import { useCacheStore } from '@/stores/cache.ts'

const props = defineProps({
  session: {
    type: Object as PropType<SessionList>,
    required: true,
  },
})

const cacheStore = useCacheStore()

const confirmRef = useTemplateRef<typeof ConfirmElement>('confirmRef')

function getVersion(version: Version) {
  let result = ''
  if (version.family) {
    result += version.family
    if (version.major) {
      result += ` ${version.major}`
      if (version.minor) {
        result += `.${version.minor}`
      }
    }
  }
  return result
}

function getIcon(item: Version) {
  const family = item.family.toLowerCase()
  // navigateurs
  if (family.search('chrome') >= 0) return PhGoogleChromeLogo
  else if (family.search('firefox') >= 0) return PhBrowser
  else if (family.search('safari') >= 0) return PhCompass
  else if (family.search('edge') >= 0) return PhBrowser
  // OS
  else if (family.search('windows') >= 0) return PhWindowsLogo
  else if (family.search('mac|ios') >= 0) return PhAppleLogo
  else if (family.search('linux') >= 0) return PhLinuxLogo
  else if (family.search('android') >= 0) return PhAndroidLogo
  return undefined
}

function getDeviceIcon(item: Device) {
  const family = item.family.toLowerCase()
  // desktop
  if (family.search('mac') >= 0) return PhLaptop
  else if (family.search('ipad') >= 0) return PhDeviceTablet
  else if (family.search('iphone|android') >= 0) return PhDeviceMobile
  return undefined
}

const emit = defineEmits(['delete'])

function confirmDeleteSession() {
  confirmRef.value?.show(
    props.session.id,
    "Suppression d'une session",
    'Êtes-vous sûr de vouloir supprimer cette session ?',
  )
}
function deleteSession() {
  emit('delete', props.session.id)
}
</script>
